import React, { useMemo } from "react";
import { Input, Spin, Checkbox } from "../../../BP2Components";
import { AutoGrowTextArea } from "./FormV3TextAreaField";
import { isFieldDisabled } from "./FormFIeldsV3Decorators";
import { useFieldController } from "../../../../util/useFieldController";
import { useOtherOption } from "../../../../util/useOtherOption";
import IntlMessages from "../../../../util/IntlMessages";

import "../formsV3.less";

const EMPTY_DECORATORS = [];

const FormV3CheckboxField = ({
   field,
   formGUID,
   loading,
   decorators,
   onValueChange,
   isQuestionnaire,
}) => {
   const { value, setValue, fieldInfo } = useFieldController({
      field,
      formGUID,
      onValueChange,
   });

   const decoratorsSource = useMemo(
      () => decorators ?? field?.decorators ?? EMPTY_DECORATORS,
      [decorators, field?.decorators],
   );

   const optionsDecorator = useMemo(() => {
      return decoratorsSource.find((decorator) => decorator?.name === "OptionsDecorator");
   }, [decoratorsSource]);

   const options = useMemo(() => {
      const rawOptions = optionsDecorator?.properties?.options;
      return Array.isArray(rawOptions) ? rawOptions : [];
   }, [optionsDecorator]);

   const otherEnabled = optionsDecorator?.properties?.other === true;

   const hasOptions = options.length > 0;

   const selectedOptions = useMemo(() => {
      if (Array.isArray(value)) return value;
      return [];
   }, [value]);

   const { isOtherSelected, otherText, toggleOther, handleOtherTextChange } = useOtherOption({
      otherEnabled,
      hasOptions,
      options,
      selectedValues: selectedOptions,
      setValue,
      mode: "multi",
   });

   const toggleOption = (optionLabel, isChecked) => {
      const nextValue = isChecked
         ? Array.from(new Set([...selectedOptions, optionLabel]))
         : selectedOptions.filter((opt) => opt !== optionLabel);

      setValue(nextValue);
   };

   const handleSingleChange = (event) => {
      setValue(event?.target?.checked);
   };

   const OtherField = isQuestionnaire ? AutoGrowTextArea : Input;

   return (
      <>
         <span
            className="v3-field-element"
            style={fieldInfo ? { borderColor: "var(--form-warnings)" } : {}}
         >
            {hasOptions ? (
               <div className="v3-options-group">
                  {options.map((optionLabel) => {
                     const isChecked = selectedOptions.includes(optionLabel);

                     return (
                        <label
                           key={`${field.id}-${optionLabel}`}
                           className={`v3-option-label v3-field id-${field.id}`}
                        >
                           <Checkbox
                              disabled={isFieldDisabled(field)}
                              className={`v3-field id-${field.id}`}
                              checked={isChecked}
                              onChange={(event) =>
                                 toggleOption(optionLabel, event?.target?.checked)
                              }
                           />
                           <span>{optionLabel}</span>
                        </label>
                     );
                  })}

                  {otherEnabled ? (
                     <div className="v3-other-container">
                        <label
                           key={`${field.id}-other`}
                           className={`v3-option-label v3-field id-${field.id}`}
                        >
                           <Checkbox
                              disabled={isFieldDisabled(field)}
                              className={`v3-field id-${field.id}`}
                              checked={!!isOtherSelected}
                              onChange={(event) => toggleOther(event?.target?.checked)}
                           />
                           <span>
                              <IntlMessages id="formField.other" />
                           </span>
                        </label>

                        {isOtherSelected && (
                           <OtherField
                              disabled={isFieldDisabled(field)}
                              value={otherText}
                              onChange={(event) => handleOtherTextChange(event.target.value)}
                              style={{ width: "100%" }}
                           />
                        )}
                     </div>
                  ) : null}
               </div>
            ) : (
               <Checkbox
                  disabled={isFieldDisabled(field)}
                  className={`v3-field id-${field.id}`}
                  checked={!!value}
                  onChange={handleSingleChange}
               />
            )}

            <span className="p2 dev-info">{field.id}</span>
            {fieldInfo ? <div className="validation-error">{fieldInfo}</div> : null}
         </span>

         {loading && <Spin />}
      </>
   );
};

export default FormV3CheckboxField;
